import { Card } from "@secretaa/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router";
import { useSession } from "../../app/session.ts";
import { LocaleSwitch } from "../../layouts/locale-switch.tsx";
import { isAuthError } from "../../shared/errors.ts";

type Theme = "system" | "light" | "dark";

function readTheme(): Theme {
  try {
    const v = localStorage.getItem("saa_theme");
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

export function applyTheme(theme: Theme) {
  if (theme === "system") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", theme);
}

export function AccountPage() {
  const { t } = useTranslation();
  const session = useSession();
  const [theme, setTheme] = useState<Theme>(readTheme);
  if (session.isPending) return <p className="p-6">{t("app.loading")}</p>;
  if (session.isError) return isAuthError(session.error) ? <Navigate to="/login" replace /> : <p className="p-6">{t("errors.generic")}</p>;
  const u = session.data.user;
  return (
    <main id="main" className="mx-auto max-w-lg p-6">
      <p className="mb-4">
        <Link className="text-sm underline" to="/select-workspace">
          {t("common.back")}
        </Link>
      </p>
      <h1 className="mb-4 text-xl font-semibold">{t("account.title")}</h1>
      <Card className="flex flex-col gap-4">
        <div>
          <p className="font-medium">{u.displayName}</p>
          <p className="text-sm text-(--saa-fg-muted)">{u.email}</p>
          {u.platformRole === "owner" ? <p className="mt-1 text-xs">{t("account.platformOwner")}</p> : null}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">{t("account.language")}</span>
          <LocaleSwitch />
        </div>
        <label className="flex items-center justify-between text-sm">
          <span>{t("account.theme")}</span>
          <select
            className="rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 py-1"
            value={theme}
            onChange={(e) => {
              const v = e.target.value as Theme;
              setTheme(v);
              applyTheme(v);
              try {
                localStorage.setItem("saa_theme", v);
              } catch {
                // trình duyệt chặn localStorage: giao diện chỉ giữ trong phiên này
              }
            }}
          >
            {(["system", "light", "dark"] as const).map((v) => (
              <option key={v} value={v}>
                {t(`account.themes.${v}`)}
              </option>
            ))}
          </select>
        </label>
      </Card>
    </main>
  );
}
